import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BrainCircuit } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.map(fmt).join(" × ");
  if (typeof v === "object") {
    if ("value" in v) return fmt(v.value);
    return JSON.stringify(v);
  }
  return String(v);
};
const short = (h) => (h ? `${String(h).slice(0, 10)}…` : "—");

export const FUSED_STYLE = {
  OK: "bg-emerald-100 text-emerald-800",
  PARSER_CONFIRMED: "bg-emerald-100 text-emerald-800",
  AI_CORROBORATED: "bg-sky-100 text-sky-800",
  AI_ONLY: "bg-violet-100 text-violet-800",
  CONFLICT: "bg-red-100 text-red-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800",
  SOURCE_MISSING: "bg-zinc-100 text-zinc-600",
  READING_READY: "bg-emerald-100 text-emerald-800",
  READING_BLOCKED: "bg-red-100 text-red-800",
};

const St = ({ s }) => <Badge className={`text-[9px] ${FUSED_STYLE[s] || "bg-zinc-100"}`}>{s || "—"}</Badge>;

/* ── confronto 3 modalità + GT ────────────────────────────────── */
const SideBySide = ({ d }) => {
  const p = d.parser_p03 || {};
  const a = d.ai_reader;
  const gtc = d.ground_truth?.counts || {};
  const gtp = d.ground_truth?.parsed || {};
  const gtCampi = {};
  (gtp.campi || []).forEach?.((c) => { gtCampi[c.key] = c.value; });
  const rows = [
    ["material", "Materiale", p.material, a?.material, gtp.materiale ?? gtCampi.materiale],
    ["thickness_mm", "Spessore (mm)", p.thickness_mm, a?.thickness_mm, gtp.spessore_mm ?? gtCampi.spessore_mm],
    ["quantity", "Quantità", p.quantity, a?.quantity, gtp.quantita ?? gtCampi.quantita],
    ["finish", "Finitura", p.finish, a?.finish, gtp.finitura ?? gtCampi.finitura],
    ["overall_dimensions_mm", "Ingombro (mm)", p.overall_dimensions_mm, a?.overall_dimensions_mm, gtp.ingombro_mm ?? gtCampi.ingombro_mm],
    ["diameter_mm", "Diametro (mm)", p.diameter_mm, a?.diameter_mm, gtp.diametro_mm ?? gtCampi.diametro_mm],
    ["holes", "Fori", p.holes, a?.holes, gtc.holes],
    ["slots", "Asole", p.slots, a?.slots, gtc.slots],
    ["bends", "Pieghe", p.bends, a?.bends, gtc.bends],
    ["rolling", "Calandratura", String(p.rolling), a ? String(a.rolling) : null, String(gtc.rolling)],
    ["phases", "Fasi", (p.phases || []).join(", "), a ? (a.phases || []).join(", ") : null, null],
  ];
  const hy = d.hybrid;
  const hf = {};
  (hy?.fields || []).forEach((f) => { hf[f.field] = f; });
  const hfe = {};
  (hy?.features || []).forEach((f) => { hfe[f.feature_type] = f; });
  const hybridCell = (key) => {
    const map = { holes: "hole", slots: "slot", bends: "bend", rolling: "rolling" };
    if (map[key]) {
      const f = hfe[map[key]];
      if (!f) return "—";
      return <span>{f.parser_count ?? "—"}/{f.ai_count ?? "—"} <St s={f.fused_state} /></span>;
    }
    const f = hf[key];
    if (!f) return "—";
    return <span>{fmt(f.value)} <St s={f.fused_state} /></span>;
  };
  return (
    <table className="w-full text-[11px]" data-testid="p03h-sidebyside-table">
      <thead><tr className="text-left text-muted-foreground">
        <th className="pr-2 py-1">Campo</th><th className="pr-2">DETERMINISTIC_ONLY (P0.3)</th>
        <th className="pr-2">AI_ONLY (Qwen3-VL)</th><th className="pr-2">HYBRID_FUSED</th><th>Ground truth</th>
      </tr></thead>
      <tbody>
        {rows.map(([k, label, pv, av, gv]) => (
          <tr key={k} className="border-t border-border align-top" data-testid={`p03h-row-${k}`}>
            <td className="pr-2 py-1 font-medium">{label}</td>
            <td className="pr-2 break-all">{fmt(pv)}</td>
            <td className="pr-2 break-all">{a ? fmt(av) : <span className="text-muted-foreground">{d.ai_status || "non eseguito"}</span>}</td>
            <td className="pr-2 break-all">{hy?.status === "OK" ? hybridCell(k) : "—"}</td>
            <td className="pr-2">{gv != null ? fmt(gv) : <span className="text-muted-foreground">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* ── fusione campo per campo ──────────────────────────────────── */
const FusionDetail = ({ d }) => {
  const hy = d.hybrid;
  if (!hy || hy.status !== "OK") {
    return <p className="text-xs text-amber-800" data-testid="p03h-fusion-missing">Fusione non disponibile: {hy?.reason || d.ai_status || "spike non eseguito"}. {hy?.note}</p>;
  }
  const rg = hy.reading_gate || {};
  return (
    <div className="space-y-3" data-testid="p03h-fusion-detail">
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold flex items-center gap-2">Gate lettura ibrido <St s={rg.status} /></h4>
        {(rg.blockers || []).map((b, i) => (
          <div key={i} className="text-[11px] py-0.5 border-b border-border/50" data-testid={`p03h-blocker-${b.code}`}>
            <b>{b.code}</b>: {b.reason} → <span className="text-sky-800">{b.next_action}</span>
          </div>
        ))}
        {(rg.blockers || []).length === 0 && <p className="text-[11px] text-emerald-800">nessun blocco</p>}
      </div>
      <table className="w-full text-[11px]" data-testid="p03h-fields-table">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Campo</th><th className="pr-2">Stato fuso</th><th className="pr-2">Parser</th><th className="pr-2">IA (conf.)</th><th className="pr-2">Valore fuso</th><th className="pr-2">Preventivo?</th><th>Spiegazione IA</th>
        </tr></thead>
        <tbody>
          {(hy.fields || []).map((f) => (
            <tr key={f.field} className="border-t border-border align-top" data-testid={`p03h-field-${f.field}`}>
              <td className="pr-2 py-1 font-medium">{f.field}</td>
              <td className="pr-2"><St s={f.fused_state} /></td>
              <td className="pr-2 break-all">{fmt(f.parser_value)}</td>
              <td className="pr-2 break-all">{fmt(f.ai_value)}{f.ai_confidence != null ? ` (${f.ai_confidence})` : ""}</td>
              <td className="pr-2 break-all">{fmt(f.value)}</td>
              <td className="pr-2">{f.quote_eligible ? "sì" : "NO"}</td>
              <td className="max-w-[260px] text-muted-foreground">{f.ai_explanation || f.note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w-full text-[11px]" data-testid="p03h-features-table">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Feature</th><th className="pr-2">Stato fuso</th><th className="pr-2">Parser</th><th className="pr-2">IA</th><th className="pr-2">Conteggio autoritativo</th><th>Preventivo?</th>
        </tr></thead>
        <tbody>
          {(hy.features || []).map((f) => (
            <tr key={f.feature_type} className="border-t border-border" data-testid={`p03h-feature-${f.feature_type}`}>
              <td className="pr-2 py-1 font-medium">{f.feature_type}</td>
              <td className="pr-2"><St s={f.fused_state} /></td>
              <td className="pr-2">{f.parser_count}</td>
              <td className="pr-2">{f.ai_count ?? "—"}</td>
              <td className="pr-2">{f.count_authoritative || "—"}</td>
              <td>{f.quote_eligible ? "sì" : "NO"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">Fasi produttive fuse ({(hy.production_phases || []).length})</h4>
        {(hy.production_phases || []).map((p, i) => (
          <div key={i} className="text-[11px] py-0.5 border-b border-border/50" data-testid={`p03h-phase-${p.operation}`}>
            <St s={p.fusion_state} /> <b>{p.operation}</b>
            {p.physical_feature && <span> · feature: {p.physical_feature}</span>}
            {p.transformation && <span> · trasformazione: {p.transformation}</span>}
            {(p.candidate_technologies || []).length > 0 && <span> · tecnologie candidate: {(p.candidate_technologies || []).join(", ")}</span>}
            <span> · macchina decisa: {String(p.machine_decided ?? false)}</span>
            {p.fusion_state === "AI_ONLY" && <span className="text-violet-800"> · {p.next_action}</span>}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground">{hy.no_silent_selection_note} · {hy.calculations?.note}</p>
    </div>
  );
};

/* ── audit chiamate ───────────────────────────────────────────── */
const CallsAudit = ({ d }) => (
  <div className="space-y-1" data-testid="p03h-calls">
    <table className="w-full text-[10px]">
      <thead><tr className="text-left text-muted-foreground">
        <th className="pr-2 py-1">Passo</th><th className="pr-2">Modello</th><th className="pr-2">HTTP</th><th className="pr-2">Latenza</th><th className="pr-2">Token (in/out)</th><th className="pr-2">Retry/riparaz.</th><th className="pr-2">Prompt sha</th><th className="pr-2">Risposta sha</th><th>Schema ok</th>
      </tr></thead>
      <tbody>
        {(d.calls || []).map((c) => (
          <tr key={c.call_id} className="border-t border-border" data-testid={`p03h-call-${c.pass}`}>
            <td className="pr-2 py-0.5 font-medium">{c.pass}</td>
            <td className="pr-2">{c.model_returned || c.model_requested}</td>
            <td className="pr-2">{c.http_status || "—"}</td>
            <td className="pr-2">{c.latency_s}s</td>
            <td className="pr-2">{c.usage ? `${c.usage.prompt_tokens}/${c.usage.completion_tokens}` : "—"}</td>
            <td className="pr-2">{c.attempts}/{c.repair_attempt ?? 0}</td>
            <td className="pr-2 font-mono">{short(c.user_text_sha256)}</td>
            <td className="pr-2 font-mono">{short(c.response_sha256)}</td>
            <td>{c.schema_valid ? "✓" : (c.error ? "✗" : "—")}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="text-[10px] text-muted-foreground">
      Prova assenza ground truth negli input: {d.calls?.[0]?.gt_absence_proof || "—"}
    </p>
    {(d.ai_errors || []).length > 0 && (
      <div className="border border-red-200 bg-red-50 p-2" data-testid="p03h-errors">
        <h4 className="text-[11px] font-semibold text-red-900">Errori chiamate ({d.ai_errors.length})</h4>
        {d.ai_errors.map((e, i) => <div key={i} className="text-[10px]">{e.pass}{e.view ? `/${e.view}` : ""}: {e.error}</div>)}
      </div>
    )}
  </div>
);

/* ── tabella decisionale (7 punti) ────────────────────────────── */
export const DecisionTableP03H = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/calibration/p03h/decision-table`, { timeout: 300000 })
      .then((r) => setData(r.data))
      .catch(() => setData({ missing: true }));
  }, []);
  if (!data) return <div className="text-sm p-2" data-testid="p03h-decision-loading">Caricamento tabella decisionale…</div>;
  if (data.missing) return null;
  const t = data.decision_table || {};
  return (
    <Card data-testid="p03h-decision-table">
      <CardHeader className="py-3"><CardTitle className="text-sm">P0.3-H — Tabella decisionale: ibrido vs solo parser ({t.overall})</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-muted-foreground">
            <th className="pr-2 py-1">Domanda</th>
            {(data.cases_evaluated || []).map((c) => <th key={c} className="pr-2">{c}</th>)}
            <th>Verdetto</th>
          </tr></thead>
          <tbody>
            {(t.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-border align-top" data-testid={`p03h-decision-row-${i}`}>
                <td className="pr-2 py-1 font-medium max-w-[240px]">{r.question}</td>
                {(data.cases_evaluated || []).map((c) => (
                  <td key={c} className="pr-2 break-all max-w-[260px]">{fmt(r.per_case?.[c])}</td>
                ))}
                <td><Badge className={`text-[9px] ${r.verdict === "SÌ" ? "bg-emerald-100 text-emerald-800" : r.verdict === "NO" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{r.verdict}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[10px] text-muted-foreground">{t.nota}</p>
        {(data.skipped || []).map((s, i) => <p key={i} className="text-[10px] text-amber-800">{s.case_id}: {s.reason}</p>)}
      </CardContent>
    </Card>
  );
};

/* ── pannello principale ──────────────────────────────────────── */
export const SpikeP03HPanel = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("confronto");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calibration/cases/${caseId}/p03h-spike`, { timeout: 300000 });
      setData(r.data);
    } catch (e) { setData({ missing: true, reason: e?.response?.data?.detail }); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm p-2" data-testid="p03h-loading">Caricamento spike P0.3-H…</div>;
  if (data.missing) {
    return (
      <Card data-testid="p03h-panel">
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><BrainCircuit size={15} /> P0.3-H — Lettore AI ibrido (SHADOW_MODE)</CardTitle></CardHeader>
        <CardContent><p className="text-xs text-muted-foreground" data-testid="p03h-missing">{data.reason || "Spike non ancora eseguito per questo caso."} Lo spike si esegue da riga di comando sul backend (nessun effetto su preventivi o gate esistenti).</p></CardContent>
      </Card>
    );
  }
  const m = data.spike_meta || {};
  const TABS = [["confronto", "Parser / IA / Ibrido / GT"], ["fusione", "Fusione & gate"], ["chiamate", "Audit chiamate AI"]];
  return (
    <Card data-testid="p03h-panel">
      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><BrainCircuit size={15} /> P0.3-H — Lettore AI ibrido (SHADOW_MODE) · stato AI: <St s={data.ai_status} /></CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="text-[11px] grid md:grid-cols-2 gap-x-6 gap-y-0.5 border border-border p-2 bg-zinc-50" data-testid="p03h-meta">
          <div>Provider: <b>{m.reader_config?.provider}</b> · modello <b>{m.reader_config?.model}</b> · modalità <b>{m.reader_config?.mode}</b></div>
          <div>run_id: <span className="font-mono">{short(m.run_id)}</span> · chiamate: <b>{m.n_calls}</b> · commit <span className="font-mono">{short(m.engine_git_commit)}</span></div>
          <div>PDF sha: <span className="font-mono">{short(m.pdf_sha256)}</span> · P0.3 output sha: <span className="font-mono">{short(m.parser_p03_output_sha256)}</span></div>
          <div className="text-amber-800">{data.dev_regression_note}</div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([k, l]) => (
            <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} className="h-7 text-xs" onClick={() => setTab(k)} data-testid={`p03h-tab-${k}`}>{l}</Button>
          ))}
        </div>
        {tab === "confronto" && <SideBySide d={data} />}
        {tab === "fusione" && <FusionDetail d={data} />}
        {tab === "chiamate" && <CallsAudit d={data} />}
      </CardContent>
    </Card>
  );
};
